import {
  SidebarDrawer,
  PlainButton,
  LoadingSpinner,
  Button,
  MobileDrawer,
  MobileDrawerProps,
} from "@jugl-web/ui-components";
import {
  useDebounce,
  useTranslations,
  useAppVariant,
  usePrevious,
} from "@jugl-web/utils";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useInView } from "react-intersection-observer";
import { Customer } from "@jugl-web/rest-api";
import { useCustomersList } from "@jugl-web/rest-api/customer/hooks/useCustomersList";
import { WITHOUT_CUSTOMER_ITEM_ID } from "@jugl-web/domain-resources/users/hooks/useCustomerListBox";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { ReactComponent as NewCustomerIcon } from "./assets/new-customer.svg";
import { ReactComponent as AddIcon } from "./assets/add.svg";
import { CustomerCard } from "./components/CustomerCard/CustomerCard";
import { ManageCustomerDialog } from "../ManageCustomerDialog";
import { CustomersProvider } from "../../providers/CustomersProvider";
import { useCustomersContext } from "../../providers/useCustomersContext";
import { CustomerSearchBar } from "../CustomerSearchBar/CustomerSearchBar";
import { WithoutCustomerCard } from "./components/WithoutCustomerCard/WithoutCustomerCard";

interface BaseCustomerPickerProps {
  entityId: string;
  isReadOnly?: boolean;
  isAdmin?: boolean;
  defaultIds?: (string | null)[];
  withoutCustomerVisible?: boolean;
  isWithoutCustomerSelected?: boolean;
  isMultiSelect?: boolean;
  onSave?: (selectedIds: string[], selectedCustomers: Customer[]) => void;
  onWithoutCustomerChange?: (isSelected: boolean) => void;
}

interface CustomerPickerSidebarContentProps extends BaseCustomerPickerProps {
  onAddNewCustomer?: () => void;
}

const CustomerPickerSidebarContent = ({
  entityId,
  isReadOnly = false,
  isAdmin,
  defaultIds,
  withoutCustomerVisible,
  isWithoutCustomerSelected,
  isMultiSelect,
  onSave,
  onWithoutCustomerChange,
  onAddNewCustomer,
}: CustomerPickerSidebarContentProps) => {
  const { isMobile } = useAppVariant();
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);

  const { t } = useTranslations();
  const { ref: bottomAnchorRef, inView: bottomAnchorInView } = useInView();

  const { searchQuery, transformedSearchBy } = useCustomersContext();
  const debouncedSearchQuery = useDebounce(searchQuery, 500) as string;

  const {
    data: customers,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
  } = useCustomersList({
    entityId,
    searchTerm: debouncedSearchQuery,
    searchBy: transformedSearchBy,
  });

  const previusBottomAnchorInView = usePrevious(bottomAnchorInView);
  useEffect(() => {
    if (
      bottomAnchorInView &&
      previusBottomAnchorInView === false &&
      hasMore &&
      !isLoading &&
      !isLoadingMore
    ) {
      loadMore();
    }
  }, [
    bottomAnchorInView,
    hasMore,
    isLoading,
    isLoadingMore,
    loadMore,
    previusBottomAnchorInView,
  ]);

  const handleSelect = useCallback(
    (customerId: string) => {
      if (selectedCustomers.includes(customerId)) {
        setSelectedCustomers((prev) => prev.filter((id) => id !== customerId));
        return;
      }
      if (isMultiSelect) {
        setSelectedCustomers((prev) => [...prev, customerId]);
      }
      if (!isMultiSelect) {
        setSelectedCustomers([customerId]);
      }
    },
    [isMultiSelect, selectedCustomers]
  );

  useEffect(() => {
    if (defaultIds) {
      setSelectedCustomers(
        defaultIds.map((id) => (id === null ? WITHOUT_CUSTOMER_ITEM_ID : id))
      );
    }
  }, [defaultIds]);

  const content = useMemo(() => {
    return (
      <>
        <CustomerSearchBar
          entityId={entityId}
          color="grey"
          className="w-full"
        />

        <div className="flex flex-col gap-4 pt-6">
          {!isReadOnly && onAddNewCustomer && (
            <div
              className="flex h-[70px] cursor-pointer items-center gap-3 rounded-[14px] px-3 hover:bg-grey-100"
              onClick={onAddNewCustomer}
            >
              <div className="flex h-[44px] w-[44px] cursor-pointer items-center justify-center rounded-full border-[1px] border-solid border-[#E0E0E0] bg-white p-[1px]">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#6192BE]">
                  <NewCustomerIcon />
                </div>
              </div>
              <div className="flex flex-col font-secondary">
                <div className="text-dark">
                  {t({
                    id: "customers-page.add-new-customer",
                    defaultMessage: "Add New Customer",
                  })}
                </div>
                <div className="text-sm text-[#828282]">
                  {isMobile
                    ? t({
                        id: "customers-page.tap-to-add",
                        defaultMessage: "Tap to add",
                      })
                    : t({
                        id: "customers-page.tap-to-add-new-customer-profile",
                        defaultMessage: "Tap to add new Customer profile",
                      })}
                </div>
              </div>
              <AddIcon className="ml-auto" />
            </div>
          )}
          {withoutCustomerVisible && !searchQuery && (
            <WithoutCustomerCard
              isSelected={selectedCustomers.includes(WITHOUT_CUSTOMER_ITEM_ID)}
              onSelect={() => {
                onWithoutCustomerChange?.(!isWithoutCustomerSelected);
                handleSelect(WITHOUT_CUSTOMER_ITEM_ID);
              }}
            />
          )}
          {customers?.map((customer) => (
            <CustomerCard
              key={customer.id}
              customer={customer}
              searchQuery={debouncedSearchQuery}
              isSelected={selectedCustomers.includes(customer.id)}
              onSelect={handleSelect}
              isAdmin={isAdmin}
            />
          ))}
        </div>
        {isLoading && <LoadingSpinner />}
        {!isLoading && customers.length === 0 && (
          <div className="flex justify-center text-sm text-grey">
            {t({
              id: "customers-page.no-customers-found",
              defaultMessage: "No customers found",
            })}
          </div>
        )}
        <div ref={bottomAnchorRef} className="h-[1px]" />
      </>
    );
  }, [
    bottomAnchorRef,
    customers,
    debouncedSearchQuery,
    entityId,
    handleSelect,
    isAdmin,
    isLoading,
    isMobile,
    isReadOnly,
    isWithoutCustomerSelected,
    onAddNewCustomer,
    onWithoutCustomerChange,
    searchQuery,
    selectedCustomers,
    t,
    withoutCustomerVisible,
  ]);

  const actions = useMemo(() => {
    return (
      <Button
        className="h-[40px] w-[300px]"
        onClick={() => {
          onSave?.(
            selectedCustomers,
            customers?.filter((c) => selectedCustomers.includes(c.id)) || []
          );
        }}
      >
        {isMultiSelect && selectedCustomers.length > 0
          ? t(
              {
                id: "customers-sidebar.save-selected-customers",
                defaultMessage: "Select {number}",
              },
              {
                number: selectedCustomers.length,
              }
            )
          : t({
              id: "common.save",
              defaultMessage: "common.save",
            })}
      </Button>
    );
  }, [customers, isMultiSelect, onSave, selectedCustomers, t]);

  if (isMobile) {
    return (
      <>
        <MobileDrawer.Content>{content}</MobileDrawer.Content>
        <MobileDrawer.Actions className="flex h-[80px] items-center justify-center border-x-0 border-t-[2px] border-b-0 border-solid border-[#EEF2F5]">
          {actions}
        </MobileDrawer.Actions>
      </>
    );
  }

  return (
    <>
      <SidebarDrawer.Content className="h-full px-8 py-6">
        {content}
      </SidebarDrawer.Content>
      {!isReadOnly && (
        <SidebarDrawer.Actions className="flex h-[80px] justify-center border-x-0 border-t-[2px] border-b-0 border-solid border-[#EEF2F5]">
          {actions}
        </SidebarDrawer.Actions>
      )}
    </>
  );
};

interface CustomerPickerSidebarInnerProps extends BaseCustomerPickerProps {
  onClose: () => void;
  onAddNewCustomerSuccess?: (data: Customer, isEdit?: boolean) => void;
}

const CustomerPickerSidebarInner = ({
  entityId,
  isReadOnly,
  isAdmin,
  defaultIds,
  withoutCustomerVisible,
  isWithoutCustomerSelected,
  isMultiSelect,
  onClose,
  onSave,
  onWithoutCustomerChange,
  onAddNewCustomerSuccess,
}: CustomerPickerSidebarInnerProps) => {
  const [isAddNewCustomerDialogOpen, setIsAddNewCustomerDialogOpen] =
    useState(false);

  return (
    <CustomersProvider entityId={entityId}>
      <CustomerPickerSidebarContent
        entityId={entityId}
        isReadOnly={isReadOnly}
        isAdmin={isAdmin}
        defaultIds={defaultIds}
        withoutCustomerVisible={withoutCustomerVisible}
        isWithoutCustomerSelected={isWithoutCustomerSelected}
        isMultiSelect={isMultiSelect}
        onSave={onSave}
        onWithoutCustomerChange={onWithoutCustomerChange}
        onAddNewCustomer={
          onAddNewCustomerSuccess
            ? () => {
                setIsAddNewCustomerDialogOpen(true);
              }
            : undefined
        }
      />
      <ManageCustomerDialog
        entityId={entityId}
        isOpen={isAddNewCustomerDialogOpen}
        onClose={() => {
          setIsAddNewCustomerDialogOpen(false);
        }}
        onSuccess={(data: Customer, isEdit?: boolean) => {
          onAddNewCustomerSuccess?.(data, isEdit);
          onClose();
        }}
        onGoBack={() => {
          setIsAddNewCustomerDialogOpen(false);
        }}
      />
    </CustomersProvider>
  );
};

type CustomerPickerSidebarProps = {
  entityId: string;
  isOpen: boolean;
  onClose: () => void;
  onAddNewCustomerSuccess?: (data: Customer, isEdit?: boolean) => void;
} & BaseCustomerPickerProps &
  (
    | { header: React.ReactNode; mobileHeader?: MobileDrawerProps["header"] }
    | { header?: React.ReactNode; mobileHeader: MobileDrawerProps["header"] }
  );

export const CustomerPickerSidebar = ({
  isOpen,
  onClose,
  header,
  mobileHeader,
  entityId,
  isReadOnly,
  isAdmin,
  defaultIds,
  withoutCustomerVisible,
  isWithoutCustomerSelected,
  isMultiSelect,
  onSave,
  onWithoutCustomerChange,
  onAddNewCustomerSuccess,
}: CustomerPickerSidebarProps) => {
  const { isMobile } = useAppVariant();

  if (isMobile) {
    return (
      <MobileDrawer
        isOpen={isOpen}
        onClose={onClose}
        header={
          mobileHeader || {
            title: header,
          }
        }
      >
        <CustomerPickerSidebarInner
          entityId={entityId}
          isReadOnly={isReadOnly}
          isAdmin={isAdmin}
          defaultIds={defaultIds}
          withoutCustomerVisible={withoutCustomerVisible}
          isWithoutCustomerSelected={isWithoutCustomerSelected}
          isMultiSelect={isMultiSelect}
          onClose={onClose}
          onSave={onSave}
          onWithoutCustomerChange={onWithoutCustomerChange}
          onAddNewCustomerSuccess={onAddNewCustomerSuccess}
        />
      </MobileDrawer>
    );
  }

  return (
    <SidebarDrawer
      isOpen={isOpen}
      onClose={onClose}
      customHeader={
        <div className="flex h-[63px] w-full shrink-0 items-center justify-between gap-2 border-0 border-b-[1px] border-solid border-dark-100 px-8">
          {header}
          <div className="flex gap-4">
            <PlainButton onClick={onClose}>
              <CloseIcon />
            </PlainButton>
          </div>
        </div>
      }
      className="w-[480px]"
    >
      <CustomerPickerSidebarInner
        entityId={entityId}
        isReadOnly={isReadOnly}
        isAdmin={isAdmin}
        defaultIds={defaultIds}
        withoutCustomerVisible={withoutCustomerVisible}
        isWithoutCustomerSelected={isWithoutCustomerSelected}
        isMultiSelect={isMultiSelect}
        onClose={onClose}
        onSave={onSave}
        onWithoutCustomerChange={onWithoutCustomerChange}
        onAddNewCustomerSuccess={onAddNewCustomerSuccess}
      />
    </SidebarDrawer>
  );
};
